import { useMemo, useState } from 'react';
import { Radio, X, Check, CheckCheck } from 'lucide-react';
import { useGameStore } from '@/state/gameStore';
import { ModalRoot } from '@/components/ui/ModalRoot';
import { Z_FLOATING, Z_MODALS } from '@/lib/zIndex';

/**
 * Phase 9.4 — Sprechwunsch-Center.
 *
 * Floating card unten rechts (über der MapToolbar) zeigt Counter wenn
 * pending Sprechwünsche existieren. Klick öffnet Modal mit Liste, einzeln
 * oder per "Alle annehmen" abarbeitbar.
 */
export function RadioRequestCenter() {
  const requests = useGameStore((s) => s.radioRequests ?? []);
  const acceptOne = useGameStore((s) => s.acceptRadioRequest);
  const acceptAll = useGameStore((s) => s.acceptAllRadioRequests);
  const dismiss = useGameStore((s) => s.dismissRadioRequest);

  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const pending = useMemo(
    () => requests.filter((r) => !r.acceptedAt),
    [requests],
  );

  if (pending.length === 0) return null;

  const toggle = (id: string) => {
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleAcceptSelected = () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    for (const id of ids) acceptOne(id);
    setSelected(new Set());
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={`Funkverkehr (${pending.length} offen)`}
        className="pointer-events-auto fixed bottom-4 right-4 flex items-center gap-2 rounded-full border border-amber-400/40 bg-amber-500/95 px-4 py-2.5 text-sm font-semibold text-bg-primary shadow-2xl ring-2 ring-amber-400/30 transition hover:bg-amber-400 focus:outline-none focus-visible:ring-4"
        style={{ zIndex: Z_FLOATING }}
      >
        <Radio className="h-4 w-4 animate-pulse" aria-hidden />
        Funkverkehr ({pending.length})
      </button>

      {open && (
        <ModalRoot
          onClose={() => setOpen(false)}
          zIndex={Z_MODALS}
          contentClass="w-full max-w-xl max-h-[80vh] overflow-hidden rounded-lg border border-border-strong bg-bg-secondary"
        >
          <header className="flex items-start justify-between bg-amber-500 px-5 py-4 text-bg-primary">
            <div>
              <p className="text-[10px] uppercase tracking-widest opacity-80">
                Sprechwünsche
              </p>
              <h3 className="font-display text-lg font-bold">
                Offene Funkanfragen ({pending.length})
              </h3>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Schließen"
              className="flex h-8 w-8 items-center justify-center rounded-md bg-bg-primary/15 text-bg-primary transition hover:bg-bg-primary/25"
            >
              <X className="h-3.5 w-3.5" aria-hidden />
            </button>
          </header>

          <div className="flex max-h-[55vh] flex-col">
            <ul className="flex-1 space-y-2 overflow-y-auto px-5 py-4">
              {pending.map((r) => {
                const checked = selected.has(r.id);
                return (
                  <li
                    key={r.id}
                    className={[
                      'rounded-md border p-3 text-xs transition',
                      checked
                        ? 'border-amber-400 bg-amber-500/10'
                        : 'border-border-subtle bg-bg-elevated/40',
                    ].join(' ')}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(r.id)}
                        className="mt-0.5 accent-amber-500"
                        aria-label={`${r.vehicleLabel} auswählen`}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold text-fg-primary">
                          📻 {r.vehicleLabel}
                        </p>
                        <p className="mt-0.5 text-[11px] text-fg-secondary">{r.text}</p>
                      </div>
                      <div className="flex flex-shrink-0 gap-1.5">
                        <button
                          type="button"
                          onClick={() => acceptOne(r.id)}
                          className="rounded-md bg-status-available px-2 py-1 text-[10px] font-semibold text-bg-primary transition hover:opacity-90"
                        >
                          <Check className="mr-0.5 inline h-3 w-3" aria-hidden />
                          Annehmen
                        </button>
                        <button
                          type="button"
                          onClick={() => dismiss(r.id)}
                          aria-label="Verwerfen"
                          className="flex h-7 w-7 items-center justify-center rounded-md bg-bg-elevated text-fg-muted transition hover:text-accent-emergency"
                        >
                          <X className="h-3 w-3" aria-hidden />
                        </button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>

            <div className="flex items-center gap-2 border-t border-border-subtle bg-bg-secondary px-5 py-3">
              <button
                type="button"
                onClick={handleAcceptSelected}
                disabled={selected.size === 0}
                className={[
                  'rounded-md px-3 py-2 text-xs font-semibold transition',
                  selected.size > 0
                    ? 'bg-amber-500 text-bg-primary hover:bg-amber-400'
                    : 'cursor-not-allowed bg-bg-elevated text-fg-muted',
                ].join(' ')}
              >
                <Check className="mr-1 inline h-3.5 w-3.5" aria-hidden />
                Ausgewählte annehmen ({selected.size})
              </button>
              <button
                type="button"
                onClick={() => {
                  acceptAll();
                  setOpen(false);
                }}
                className="ml-auto rounded-md bg-accent-emergency px-3 py-2 text-xs font-semibold text-white transition hover:bg-accent-emergency-hover"
              >
                <CheckCheck className="mr-1 inline h-3.5 w-3.5" aria-hidden />
                Alle annehmen
              </button>
            </div>
          </div>
        </ModalRoot>
      )}
    </>
  );
}
